// The claims TSX reads from a Casper token, and the gate a tab's token must pass to be sent upstream instead of the
// __mpt cookie (MPH-1085). Pure on purpose: the server (ensureFreshCasperSession), the proxy (the x-tsx-mpt-renew
// signal) and the browser (the tab's own token, MPH-1410) all import it, so it reads nothing from Next or the AppFw.

export const CASPER_SESSION_EXPIRY_BUFFER_SECS = 30;

export type CasperTokenClaims = {
  sessionId: string;
  exp: number;
  hasAccountId: boolean;
  isSignedIn: boolean;
  // ua.hid, the hashed customer id: '' when the token names no signed-in traveller.
  hashedId: string;
};

// base64url to text. Node and the proxy have Buffer; the browser decodes through atob and percent-encoding, which
// restores UTF-8 without TextDecoder (missing from some test environments). The input is turned into plain base64
// first, so a Buffer polyfill that does not know 'base64url' still decodes it.
function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  if (typeof Buffer !== 'undefined') return Buffer.from(padded, 'base64').toString('utf8');
  const binary = atob(padded);
  let percentEncoded = '';
  for (let i = 0; i < binary.length; i += 1) {
    percentEncoded += `%${binary.charCodeAt(i).toString(16).padStart(2, '0')}`;
  }
  return decodeURIComponent(percentEncoded);
}

// Decoded locally, not through the AppFw's decodeJWT: that helper console.logs the whole raw token
// when parsing fails, and __mpt holds anything a caller passed in the `mpt` URL parameter. Never logs either.
export function readCasperTokenClaims(mpt: string): CasperTokenClaims | undefined {
  if (!mpt) return undefined;
  try {
    const payload = JSON.parse(decodeBase64Url(mpt.split('.')[1]));
    // exp must be a real number, not merely truthy: isSupersededBy compares it with `>`, and two
    // numeric strings would compare lexicographically ("9..." > "10...") and invert that decision.
    if (typeof payload?.exp !== 'number') return undefined;
    return {
      sessionId: typeof payload.id === 'string' ? payload.id : '',
      exp: payload.exp,
      hasAccountId: !!payload['ua.aid'],
      isSignedIn: !!payload['ua.hid'],
      hashedId: typeof payload['ua.hid'] === 'string' ? payload['ua.hid'] : '',
    };
  } catch {
    return undefined;
  }
}

// nowSecs lets the browser judge freshness by the server's clock rather than its own (MPH-1410).
export function isCasperTokenFresh(
  claims: CasperTokenClaims | undefined,
  nowSecs: number = Date.now() / 1000
): boolean {
  return !!claims && nowSecs < claims.exp - CASPER_SESSION_EXPIRY_BUFFER_SECS;
}

// What shopping-hdp-api alerts on (MPH-1145): a token that says the traveller is signed in but carries
// no account id.
export function isSignedInWithoutAccountId(claims: CasperTokenClaims | undefined): boolean {
  return !!claims?.isSignedIn && !claims.hasAccountId;
}

// Both tokens Casper mints for one session carry the same `id`, so matching ids only prove the
// candidate belongs to the session the cookie still names - not that the traveller is still signed
// in: an observed sign-out kept id, uuid, ua.se and urd, dropping only ua.aid. See isSupersededBy.
export function namesSameCasperSession(
  candidate: CasperTokenClaims | undefined,
  reference: CasperTokenClaims | undefined
): boolean {
  return !!candidate?.sessionId && candidate.sessionId === reference?.sessionId;
}

// The session id survives a sign-out, so it cannot tell two travellers on one browser apart; the hashed
// customer id can. Compared only when both tokens carry one, so a token without it still relies on the
// session and expiry checks alone.
export function namesSameTraveller(candidate: CasperTokenClaims, reference: CasperTokenClaims | undefined): boolean {
  return !candidate.hashedId || !reference?.hashedId || candidate.hashedId === reference.hashedId;
}

// The ua.aid claim is the only thing the header token adds, so requiring it keeps every other
// request - anonymous traffic, which is most of it - on the cookie the server set itself rather than
// on a value the caller supplied. It also makes this self-disabling: the day Casper puts ua.aid in
// the cookie too, the cookie stops being the poorer token and wins again with no change here.
export function addsAccountId(
  candidate: CasperTokenClaims | undefined,
  reference: CasperTokenClaims | undefined
): boolean {
  return !!candidate?.hasAccountId && !reference?.hasAccountId;
}

// Casper stamps the same `exp` on both tokens in one response, so a later cookie expiry means a
// later Casper call - a sign-in, sign-out or renewal since the page presenting this token rendered.
export function isSupersededBy(
  candidate: CasperTokenClaims | undefined,
  reference: CasperTokenClaims | undefined
): boolean {
  return !!candidate && !!reference && reference.exp > candidate.exp;
}

export type HeaderRejectionReason =
  'no_header' | 'unreadable' | 'expired' | 'no_account_id' | 'other_session' | 'superseded';

// The gate the header token must pass to be sent instead of the cookie, in order: the first check it
// fails, or undefined when it passes. The gate and the reason logged for it are the same decision.
export function getHeaderRejectionReason(
  mptHeaderValue: string,
  headerClaims: CasperTokenClaims | undefined,
  cookieClaims: CasperTokenClaims | undefined
): HeaderRejectionReason | undefined {
  if (!mptHeaderValue) return 'no_header';
  if (!headerClaims) return 'unreadable';
  if (!isCasperTokenFresh(headerClaims)) return 'expired';
  if (!addsAccountId(headerClaims, cookieClaims)) return 'no_account_id';
  if (!namesSameCasperSession(headerClaims, cookieClaims)) return 'other_session';
  if (isSupersededBy(headerClaims, cookieClaims)) return 'superseded';
  return undefined;
}

// MPH-1410: the reason the proxy sends back as x-tsx-mpt-renew, so the tab renews its token. Exactly the case the
// __tsx_mpt copy rescues today: the request carries a tab token, the cookie says the traveller is signed in without
// an account id, and the gate refuses the tab token. Anything else - no tab token, an anonymous cookie, a tab token
// the gate accepts - gets no signal, because renewing would change nothing upstream.
// A tab token that is itself signed in without an account id still gets the signal (c2): a renewal may bring back
// ua.aid, and the tab limits signal renewals to one every two minutes, which bounds the cost while Casper gives this
// traveller no ua.aid.
export function getRenewSignalReason(
  mptHeaderValue: string,
  mptCookieValue: string
): HeaderRejectionReason | undefined {
  if (!mptHeaderValue) return undefined;
  const cookieClaims = readCasperTokenClaims(mptCookieValue);
  if (!isSignedInWithoutAccountId(cookieClaims)) return undefined;
  return getHeaderRejectionReason(mptHeaderValue, readCasperTokenClaims(mptHeaderValue), cookieClaims);
}
